<template>
  <v-container :fluid="$vuetify.display.mdAndDown">
    <alert-card
        v-if="alert.visible === true"
        v-model:visible="alert.visible"
        :type="alert.type"
        :title="alert.title"
        :text="alert.text"
    />
    <car-list-filter
        :year-options="yearItems"
        :filterLoadingStates="filterLoadingStates"
        :filterOptions="filterOptions"
        v-model:manufacturer="filter.manufacturer"
        v-model:model="filter.model"
        v-model:generation="filter.generation"
        v-model:wheel-drive="filter.wheelDrive"
        v-model:specification="filter.specification"
        v-model:fuel="filter.fuel"
        v-model:transmission="filter.transmission"
        v-model:engine-from="filter.engineFrom"
        v-model:engine-to="filter.engineTo"
        v-model:yearFrom="filter.yearFrom"
        v-model:yearTo="filter.yearTo"
        v-model:priceFrom="filter.priceFrom"
        v-model:priceTo="filter.priceTo"
    />
    <car-list
        :items="items"
        :loading="loading"
    />
    <v-pagination
        v-model="this.page"
        :length="this.pageAmount"
    />
  </v-container>
</template>

<script>
import CarList from "@/components/Car/CarList.vue";
import {auth} from "@/common/auth";
import {api} from "@/common/api";
import CarListFilter from "@/components/Car/CarListFilter.vue";
import debounce from 'lodash.debounce'
import {messages} from "@/common/enum/messages";
import AlertCard from "@/components/AlertCard.vue";
import {numbers} from "@/common/numbers";
import {CONFIG} from "@/common/config";
import moment from "moment/moment";
import {inject} from "vue";
import {strings} from "@/common/strings";

export default {
  name: "HomeView",
  components: {AlertCard, CarListFilter, CarList},
  data() {
    return {
      filterOptions: {
        manufacturers: [],
        models: [],
        generations: [],
        specifications: [],
        wheelDrives: [],
        filteredSpecifications: [],
        fuels: [],
        transmissions: []
      },
      filter: {
        manufacturer: null,
        model: null,
        generation: null,
        wheelDrive: null,
        specification: null,
        fuel: null,
        transmission: null,
        engineFrom: null,
        engineTo: null,
        priceFrom: null,
        priceTo: null,
        yearFrom: null,
        yearTo: null
      },
      filterLoadingStates: {
        manufacturers: true,
        models: false,
        generations: false,
        specifications: false,
        fuels: true,
        transmissions: true
      },
      requestStates: {
        carsRequest: null,
        manufacturersRequest: null,
        modelsRequest: null,
        generationsRequest: null,
        specificationsRequest: null,
        fuelsRequest: null,
        transmissionsRequest: null
      },
      page: 1,
      limit: 10,
      items: [],
      total: 1,
      loading: false,
      alert: {
        visible: false,
        initiator: null,
        type: 'error',
        title: 'Error',
        text: ''
      }
    };
  },
  computed: {
    pageAmount() {
      return Math.ceil(this.total / this.limit)
    },
    yearItems() {
      return Array(moment().year() - 1950 + 1)
          .fill(moment().year())
          .map((year, index) => year - index)
    }
  },
  watch: {
    page() {
      this.loadCars(this.filter)
    },
    'filter.manufacturer'(manufacturer) {
      if (!manufacturer) {
        return
      }
      this.filterOptions.models = []
      this.filterOptions.generations = []
      this.filterOptions.specifications = []
      this.filterOptions.wheelDrives = []
      this.filterOptions.filteredSpecifications = []
      this.filter.model = null
      this.filter.generation = null
      this.filter.wheelDrive = null
      this.filter.specification = null
      this.loadModels(manufacturer)
    },
    'filter.model'(model) {
      if (!model) {
        return
      }
      this.filterOptions.generations = []
      this.filterOptions.specifications = []
      this.filterOptions.wheelDrives = []
      this.filterOptions.filteredSpecifications = []
      this.filter.generation = null
      this.filter.wheelDrive = null
      this.filter.specification = null
      this.loadGenerations(model)
    },
    'filter.generation'(generation) {
      if (!generation) {
        return
      }
      this.filterOptions.specifications = []
      this.filterOptions.wheelDrives = []
      this.filterOptions.filteredSpecifications = []
      this.filter.wheelDrive = null
      this.filter.specification = null
      this.loadSpecifications(generation)
    },
    'filter.wheelDrive'(wheelDrive) {
      if (wheelDrive !== false && wheelDrive !== true) {
        return
      }
      this.filter.specification = null
      this.filterOptions.filteredSpecifications = this.filterOptions.specifications.filter((specification) => specification.isFullWD === wheelDrive)
    },
    filter: {
      handler: debounce(function (filter) {
        this.loadCars(filter)
      }, 500),
      deep: true,
      immediate: true
    }
  },
  setup() {
    return {
      switchAuthorizationState: inject('authorization').switchState
    }
  },
  mounted() {
    this.loadCars(this.filter)
    this.loadManufacturers()
    this.loadFuels()
    this.loadTransmissions()
  },
  methods: {
    //filters
    loadManufacturers() {
      if (this.requestStates.manufacturersRequest && !this.requestStates.manufacturersRequest.signal.aborted) {
        this.requestStates.manufacturersRequest.abort()
      }

      this.requestStates.manufacturersRequest = new AbortController()
      this.filterLoadingStates.manufacturers = true

      const token = auth.getToken()
      api.getFilters(token, 'manufacturers', {}, this.requestStates.manufacturersRequest.signal, (response) => {
        this.filterLoadingStates.manufacturers = false
        if (response?.data?.status === 'error') {
          this.alert.initiator = 'manufacturer'
          this.alert.visible = true
          this.alert.type = 'error'
          this.alert.title = 'Error'
          this.alert.text = response.data?.message?.length === 0 ? messages.en.cars_view.manufacturers_loading_error : response.data.message
          return
        }

        this.resetAlert('manufacturer')
        const filters = response.data.filters
        this.filterOptions.manufacturers = filters.map(manufacturer => {
          return {
            value: manufacturer.id,
            title: manufacturer.english_name
          }
        })
      })
    },
    loadModels(manufacturer) {
      if (this.requestStates.modelsRequest && !this.requestStates.modelsRequest.signal.aborted) {
        this.requestStates.modelsRequest.abort()
      }

      this.requestStates.modelsRequest = new AbortController()
      this.filterLoadingStates.models = true

      const token = auth.getToken()
      api.getFilters(token, 'models', {manufacturer_id: manufacturer}, this.requestStates.modelsRequest.signal, (response) => {
        this.filterLoadingStates.models = false
        if (response?.data?.status === 'error') {
          this.alert.initiator = 'model'
          this.alert.visible = true
          this.alert.type = 'error'
          this.alert.title = 'Error'
          this.alert.text = response.data?.message?.length === 0 ? messages.en.cars_view.models_loading_error : response.data.message
          return
        }

        this.resetAlert('model')
        const filters = response.data.filters
        this.filterOptions.models = filters.map(model => {
          return {
            value: model.id,
            title: model.english_name
          }
        })
      })
    },
    loadGenerations(model) {
      if (this.requestStates.generationsRequest && !this.requestStates.generationsRequest.signal.aborted) {
        this.requestStates.generationsRequest.abort()
      }

      this.requestStates.generationsRequest = new AbortController()
      this.filterLoadingStates.generations = true

      const token = auth.getToken()
      api.getFilters(token, 'generations', {model_id: model}, this.requestStates.generationsRequest.signal, (response) => {
        this.filterLoadingStates.generations = false
        if (response?.data?.status === 'error') {
          this.alert.initiator = 'generations'
          this.alert.visible = true
          this.alert.type = 'error'
          this.alert.title = 'Error'
          this.alert.text = response.data?.message?.length === 0 ? messages.en.cars_view.generations_loading_error : response.data.message
          return
        }

        this.resetAlert('generation')
        const filters = response.data.filters
        this.filterOptions.generations = filters.map(generation => {
          return {
            value: generation.id,
            title: generation.english_name
          }
        })
      })
    },
    loadSpecifications(generation) {
      if (this.requestStates.specificationsRequest && !this.requestStates.specificationsRequest.signal.aborted) {
        this.requestStates.specificationsRequest.abort()
      }

      this.requestStates.specificationsRequest = new AbortController()
      this.filterLoadingStates.specifications = true

      const token = auth.getToken()
      api.getFilters(token, 'specifications', {generation_id: generation}, this.requestStates.specificationsRequest.signal, (response) => {
        this.filterLoadingStates.specifications = false
        if (response?.data?.status === 'error') {
          this.alert.initiator = 'wheel-drives'
          this.alert.visible = true
          this.alert.type = 'error'
          this.alert.title = 'Error'
          this.alert.text = response.data?.message?.length === 0 ? messages.en.cars_view.specifications_loading_error : response.data.message
          return
        }

        this.resetAlert('specifications')
        const filters = response.data.filters
        this.filterOptions.specifications = filters.map(specification => {
          return {
            value: specification.id,
            title: specification.english_name,
            isFullWD: specification.is_full_w_d
          }
        })

        const self = this;
        filters.forEach(function (filter) {
          if (self.filterOptions.wheelDrives.filter(wheelDrive => filter.is_full_w_d == wheelDrive.value).length === 0) {
            self.filterOptions.wheelDrives.push({
              value: filter.is_full_w_d,
              title: filter.is_full_w_d ? '4WD' : '2WD',
            })
          }
        })
      })
    },
    loadFuels() {
      if (this.requestStates.fuelsRequest && !this.requestStates.fuelsRequest.signal.aborted) {
        this.requestStates.fuelsRequest.abort()
      }

      this.requestStates.fuelsRequest = new AbortController()
      this.filterLoadingStates.fuels = true

      const token = auth.getToken()
      api.getFilters(token, 'fuels', {}, this.requestStates.fuelsRequest.signal, (response) => {
        this.filterLoadingStates.fuels = false
        if (response?.data?.status === 'error') {
          this.alert.initiator = 'fuel'
          this.alert.visible = true
          this.alert.type = 'error'
          this.alert.title = 'Error'
          this.alert.text = response.data?.message?.length === 0 ? messages.en.cars_view.fuels_loading_error : response.data.message
          return
        }

        this.resetAlert('fuel')
        const filters = response.data.filters
        this.filterOptions.fuels = filters.map(fuel => {
          return {
            value: fuel.id,
            title: fuel.english_name
          }
        })
      })
    },
    loadTransmissions() {
      if (this.requestStates.transmissionsRequest && !this.requestStates.transmissionsRequest.signal.aborted) {
        this.requestStates.transmissionsRequest.abort()
      }

      this.requestStates.transmissionsRequest = new AbortController()
      this.filterLoadingStates.transmissions = true
      const token = auth.getToken()
      api.getFilters(token, 'transmissions', {}, this.requestStates.transmissionsRequest.signal, (response) => {
        this.filterLoadingStates.transmissions = false
        if (response?.data?.status === 'error') {
          this.alert.initiator = 'transmission'
          this.alert.visible = true
          this.alert.type = 'error'
          this.alert.title = 'Error'
          this.alert.text = response.data?.message?.length === 0 ? messages.en.cars_view.transmissions_loading_error : response.data.message
          return
        }

        this.resetAlert('transmission')
        const filters = response.data.filters
        this.filterOptions.transmissions = filters.map(transmission => {
          return {
            value: transmission.id,
            title: transmission.english_name
          }
        })
      })
    },

    //cars
    loadCars(filter) {
      if (this.requestStates.carsRequest && !this.requestStates.carsRequest.signal.aborted) {
        this.requestStates.carsRequest.abort()
      }

      this.requestStates.carsRequest = new AbortController()
      this.loading = true
      const token = auth.getToken()
      api.getCars(token, {
        page: this.page,
        limit: this.limit,
        manufacturer_id: filter.manufacturer,
        model_id: filter.model,
        generation_id: filter.generation,
        full_wd: filter.wheelDrive,
        specification_id: filter.specification,
        fuel_id: filter.fuel,
        transmission_id: filter.transmission,
        engine_from: filter.engineFrom,
        engine_to: filter.engineTo,
        year_from: filter.yearFrom,
        year_to: filter.yearTo,
        price_from: filter.priceFrom,
        price_to: filter.priceTo
      }, this.requestStates.carsRequest.signal, this.setCars)
    },
    setCars(response) {
      this.loading = false
      if (response?.data?.status === 'error') {
        this.alert.initiator = 'car'
        this.alert.visible = true
        this.alert.type = 'error'
        this.alert.title = 'Error'
        this.alert.text = response.data?.message?.length === 0 ? messages.en.cars_view.cars_loading_error : response.data.message
        return
      }

      this.resetAlert('car')
      const cars = response.data.cars
      this.total = response.data.total
      this.items = cars.map(car => {
        return {
          id: car.id,
          title: car.manufacturer.english_name + ' ' + car.model.english_name + ', ' + car.year.value,
          prices: car.public_prices,
          subtitle: car.specification.english_name + ', ' + car.engine + 'cc, ' + numbers.addGapsBetweenPlaces(car.mileage.value) + ' km',
          description: strings.cutLongDescription(car.description),
          mainPhoto: CONFIG.API_HOST + '/api/v1/car-photos/' + car.main_photo.id
        }
      })
    },
    resetAlert(initiator) {
      if (this.alert.initiator === initiator) {
        this.alert.visible = false
      }
    }
  },
}
</script>

<style scoped>

</style>